/**
 * Corridor reference data — copy + facts used by both CorridorMap (homepage)
 * and the dedicated /corridors/:id landing pages.
 *
 * Coordinates are [longitude, latitude] (GeoJSON convention).
 */

export const HUB = {
    name: "India",
    coords: [78.96, 22.5],
};

export const CORRIDORS = [
    {
        id: "usa",
        slug: "usa",
        country: "USA",
        flag: "🇺🇸",
        coords: [-95.71, 39.5],
        currencies: ["USD"],
        settlement: "<24 hrs",
        delivery: "Same-day for ACH cut-offs · SWIFT MT103 same-day",
        rails: ["ACH", "FedWire", "SWIFT gpi"],
        tagline: "Trade with the world's largest economy — without the bank tax.",
        kpis: [
            { k: "Avg INR credit", v: "21h" },
            { k: "TruoPay margin", v: "0.30%" },
            { k: "Bank markup saved", v: "~2.5%" },
        ],
        benefits: [
            "Receive USD payouts from Stripe, PayPal, Wise, Mercury and direct bank wires.",
            "Pay US vendors via ACH with auto-generated IDPMS filings.",
            "Auto-issued FIRA / FIRC on every inward USD credit.",
        ],
        useCases: [
            { title: "SaaS payouts", desc: "Sweep US Stripe payouts into INR at mid-market." },
            { title: "Marketplace sellers", desc: "Collect from Amazon US, Shopify, Etsy at one cost." },
            { title: "Import payments", desc: "Pay US suppliers in USD with auto-IDPMS." },
        ],
    },
    {
        id: "uk",
        slug: "uk",
        country: "UK",
        flag: "🇬🇧",
        coords: [-1.62, 53.5],
        currencies: ["GBP"],
        settlement: "<24 hrs",
        delivery: "FPS instant on supported banks · SWIFT MT103 same-day",
        rails: ["FPS", "BACS", "SWIFT gpi"],
        tagline: "Move money between Mumbai and Manchester in minutes — not days.",
        kpis: [
            { k: "Avg INR credit", v: "18h" },
            { k: "TruoPay margin", v: "0.35%" },
            { k: "Bank markup saved", v: "~2.8%" },
        ],
        benefits: [
            "Collect GBP from UK clients with auto FIRA/FIRC.",
            "Pay UK SaaS, agencies and contractors via FPS.",
            "Multi-currency wallet so you can hold GBP until you choose to convert.",
        ],
        useCases: [
            { title: "Agencies", desc: "Bill UK retainers in GBP, convert when rates are favourable." },
            { title: "Freelancers", desc: "Receive Wise GBP balances at flat 0.35%." },
            { title: "Importers", desc: "Pay UK suppliers in GBP with full audit trail." },
        ],
    },
    {
        id: "canada",
        slug: "canada",
        country: "Canada",
        flag: "🇨🇦",
        coords: [-106.34, 56.13],
        currencies: ["CAD"],
        settlement: "<24–48 hrs",
        delivery: "EFT next-day · SWIFT MT103 same-day",
        rails: ["EFT", "SWIFT gpi"],
        tagline: "Open up the Canadian market without the cross-border drag.",
        kpis: [
            { k: "Avg INR credit", v: "32h" },
            { k: "TruoPay margin", v: "0.40%" },
            { k: "Bank markup saved", v: "~3.0%" },
        ],
        benefits: [
            "Collect CAD payouts via EFT and SWIFT directly into multi-currency wallet.",
            "Pay Canadian SaaS, vendors and contractors at transparent FX.",
            "Auto Schedule III + Form A2 paperwork on every outward payment.",
        ],
        useCases: [
            { title: "Tech services", desc: "Bill Canadian enterprises in CAD with FIRC." },
            { title: "Exporters", desc: "Receive CAD invoices into INR without bank haircut." },
            { title: "LRS", desc: "Tuition transfers to Canadian universities under LRS." },
        ],
    },
    {
        id: "middle-east",
        slug: "middle-east",
        country: "Middle East",
        flag: "🇦🇪",
        coords: [54.5, 25.5],
        currencies: ["AED", "SAR", "QAR"],
        settlement: "<24 hrs",
        delivery: "Same-day AED corridor · SWIFT MT103 same-day",
        rails: ["IBAN/SWIFT", "UAEFTS"],
        tagline: "The Gulf is your second home market. We make it feel like it.",
        kpis: [
            { k: "Avg INR credit", v: "19h" },
            { k: "TruoPay margin", v: "0.40%" },
            { k: "Bank markup saved", v: "~3.0%" },
        ],
        benefits: [
            "Collect AED / SAR / QAR payments from UAE, KSA and Qatari counterparties.",
            "Pay Gulf vendors and partners via UAEFTS and SWIFT.",
            "Compliance support for purpose codes specific to GCC corridors.",
        ],
        useCases: [
            { title: "GCC importers", desc: "Pay UAE suppliers same-day in AED." },
            { title: "Construction & EPC", desc: "Receive milestone payouts in AED/SAR." },
            { title: "Trading houses", desc: "Multi-leg corridor routing via UAE." },
        ],
    },
    {
        id: "china",
        slug: "china",
        country: "China",
        flag: "🇨🇳",
        coords: [104.19, 35.86],
        currencies: ["CNY", "USD"],
        settlement: "<48 hrs",
        delivery: "CIPS next-day for CNY · SWIFT MT103 same-day for USD",
        rails: ["CIPS", "SWIFT gpi"],
        tagline: "Bridge the world's largest manufacturing economy with India's fastest-growing trade hub.",
        kpis: [
            { k: "Avg INR credit", v: "36h" },
            { k: "TruoPay margin", v: "0.45%" },
            { k: "Bank markup saved", v: "~3.2%" },
        ],
        benefits: [
            "Pay Chinese suppliers in CNY directly via CIPS — skip the USD intermediary leg.",
            "Receive CNY / USD inflows with auto FIRA / FIRC on every credit.",
            "Auto purpose-code mapping for IDPMS imports from PRC counterparties.",
        ],
        useCases: [
            { title: "Electronics importers", desc: "Pay PRC factory suppliers in CNY with full IDPMS docs." },
            { title: "D2C brands", desc: "Settle Alibaba / 1688 vendor invoices at flat 0.45%." },
            { title: "Trading houses", desc: "USD ↔ CNY routing with single FX leg." },
        ],
    },
    {
        id: "japan",
        slug: "japan",
        country: "Japan",
        flag: "🇯🇵",
        coords: [138.25, 36.2],
        currencies: ["JPY"],
        settlement: "<24 hrs",
        delivery: "Zengin same-day on supported banks · SWIFT MT103 same-day",
        rails: ["Zengin", "SWIFT gpi"],
        tagline: "Connect India's services to Japan's enterprise economy.",
        kpis: [
            { k: "Avg INR credit", v: "22h" },
            { k: "TruoPay margin", v: "0.40%" },
            { k: "Bank markup saved", v: "~2.9%" },
        ],
        benefits: [
            "Receive JPY payouts from Japanese enterprises directly via Zengin and SWIFT.",
            "Pay Japanese contractors and SaaS in JPY with auto-generated Form A2 paperwork.",
            "Multi-currency wallet so you can hold JPY through volatile USD/JPY swings.",
        ],
        useCases: [
            { title: "IT services exports", desc: "Bill Japanese enterprises in JPY with auto FIRC." },
            { title: "Manufacturing", desc: "Pay precision component suppliers same-day in JPY." },
            { title: "Marketplace sellers", desc: "Repatriate Rakuten / Amazon JP earnings at mid-market." },
        ],
    },
    {
        id: "south-africa",
        slug: "south-africa",
        country: "South Africa",
        flag: "🇿🇦",
        coords: [22.94, -30.56],
        currencies: ["ZAR"],
        settlement: "<24–48 hrs",
        delivery: "RTC same-day on supported banks · SWIFT MT103 same-day",
        rails: ["RTC", "SARB SWIFT gpi"],
        tagline: "Africa's gateway, served at India costs.",
        kpis: [
            { k: "Avg INR credit", v: "30h" },
            { k: "TruoPay margin", v: "0.45%" },
            { k: "Bank markup saved", v: "~3.4%" },
        ],
        benefits: [
            "Collect ZAR inflows from South African clients with full SARB-compliant audit trail.",
            "Pay South African vendors and SaaS via RTC at transparent mid-market FX.",
            "SARB BoP-category mapping auto-attached to every outward remittance.",
        ],
        useCases: [
            { title: "Africa-focused exporters", desc: "Receive ZAR invoices from SA enterprises into INR." },
            { title: "Mining & resources trade", desc: "Settle ZAR-denominated commodity payouts." },
            { title: "Tech services", desc: "Bill SA fintech and SaaS clients in ZAR at flat 0.45%." },
        ],
    },
];

export const getCorridor = (id) =>
    CORRIDORS.find((c) => c.id === id || c.slug === id);
